import {
  SubmitPushToCardTransferResult,
  SubmitTransferResult,
} from '@varo/graphql-schema-types';
import {
  correlationIdOf,
  describeCause,
  flowNameAttributes,
  operationErrorMessage,
  rejectionMessage,
  TransferSuccessfulAttributes,
} from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import {
  TRANSFER_MONEY_CLIENT_ERROR,
  TRANSFER_MONEY_GRAPHQL_OPERATION,
  TRANSFER_MONEY_SUCCESSFUL,
} from 'shared/analytics/events/transferMoney';
import { useAnalytics } from 'shared/hooks/analytics/useAnalytics';
import { CombinedError, OperationResult } from 'urql';

type SubmitOperationTag = 'SubmitTransfer' | 'SubmitPushToCardTransfer';

type SubmitClientErrorTag =
  | 'SubmitTransferThrew'
  | 'SubmitPushToCardThrew'
  | 'TransferQuoteTokenMissing'
  | 'UnsupportedTransferRail'
  | 'HomeTileRefreshFailed';

export interface SubmitAnalyticsSource {
  source_name: string;
  screen: string;
}

export default function useSubmitTransferAnalytics(
  source: SubmitAnalyticsSource,
  flowName?: string,
) {
  const { logEvent } = useAnalytics();
  const baseAttributes = { ...source, ...flowNameAttributes(flowName) };

  const logOperationResult = (
    tag: SubmitOperationTag,
    result: SubmitTransferResult | SubmitPushToCardTransferResult | undefined,
    isSuccess: boolean,
    extensions: OperationResult['extensions'],
  ) => {
    void logEvent(TRANSFER_MONEY_GRAPHQL_OPERATION, {
      ...baseAttributes,
      tag,
      outcome: isSuccess ? 'Success' : 'Failure',
      typename: result?.__typename,
      correlation_id: correlationIdOf(extensions),
      error_message: rejectionMessage(result),
    });
  };

  const logOperationError = (
    tag: SubmitOperationTag,
    error: CombinedError,
    extensions: OperationResult['extensions'],
  ) => {
    void logEvent(TRANSFER_MONEY_GRAPHQL_OPERATION, {
      ...baseAttributes,
      tag,
      outcome: 'Failure',
      correlation_id: correlationIdOf(extensions, error),
      error_message: operationErrorMessage(error),
    });
  };

  const logClientError = (tag: SubmitClientErrorTag, cause?: unknown) => {
    void logEvent(TRANSFER_MONEY_CLIENT_ERROR, {
      ...baseAttributes,
      tag,
      error_message: describeCause(cause),
    });
  };

  const logTransferSuccessful = (attributes: TransferSuccessfulAttributes) => {
    void logEvent(TRANSFER_MONEY_SUCCESSFUL, {
      ...baseAttributes,
      ...attributes,
    });
  };

  return {
    logOperationResult,
    logOperationError,
    logClientError,
    logTransferSuccessful,
  };
}
